import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AssessmentContext } from "../../contexts/assessments";
import { ReportAccessContext } from "../../contexts/reportAccess";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import WelcomeReport from "../../components/WelcomeReport";
import BannerReport from "../../components/BannerReport";
import BlockReport from "../../components/BlockReport";
import bgImage from "../../assets/images/bgImage.png";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GroupContext } from "../../contexts/group";
import { getAllAssessment, getCompleteDate, getUserTIMRol, getUserEvalInfo, createAnswer, updateAnswer, getExtraAnswer, getGroupById } from "../../axios/axiosFunctions";
import { UserContext } from "../../contexts/user";
import BlockReportTIM from "../../components/BlockReportTIM";
import BlockReport360 from "../../components/BlockReport360";
import BlockReportTIMSpecial from "../../components/BlockReportTIMSpecial";
import SaveReminder from "../../components/SaveReminder";
import BlockReportIntro from "../../components/BlockReportIntro";
import Logo from "../../assets/images/K2GLogo2.svg";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import AssessmentPDF from "../../components/PDF/AssessmentPDF";
import AssessmentTIMPDF from "../../components/PDF/AssessmentTIMPDF";
import Assessment360PDF from "../../components/PDF/Assessment360PDF";
import BlockEndTIM from "../../components/BlockEndTIM";
import BlockReport360Intro from "../../components/BlockReport360Intro";
import BlockEnd360 from "../../components/BlockEnd360";
import BlockEndSelf from "../../components/BlockEndSelf";
import BannerReportTIM from "../../components/BannerReportTIM";
import AppendixTIM from "../../components/AppendixTIM";
import AppendixTIMPDF from "../../components/PDF/AppendixTIMPDF";
import AppendixSelf from "../../components/AppendixSelf";
import AppendixSelfPDF from "../../components/PDF/AppendixSelfPDF";
import mimAppendix from "../../data/mimAppendix.json";
import limAppendix from "../../data/limAppendix.json";
import { isTimFamilySlug } from "../../utils/timFamily";
import {
  ReportEditContext,
  useReportEditValue,
  curateVerbatim,
} from "../../contexts/reportEdit";
import ReportEditToolbar from "../../components/ReportEditToolbar";

// Self-assessment slugs that have a module-by-module PD appendix.
const SELF_APPENDIX_DATA = {
  mim_self_assessment: mimAppendix,
  lim_self_assessment: limAppendix,
};

const ReportAssessment = () => {
  const navigate = useNavigate();
  const { slugAssessment, groupId, reportUserId } = useParams();
  const { getAssessmentBySlug, isLoading } = useContext(AssessmentContext);
  const { currentGroup } = useContext(GroupContext);
  const { currentUser } = useContext(UserContext);
  const { share, clearShare } = useContext(ReportAccessContext);
  const isGuest = !currentUser && !!share;
  const guestViewerUserId = share?.viewerUserId || null;
  const [currentAssessment, setCurrentAssessment] = useState();
  const [blocks, setBlocks] = useState([]);
  const [chartData, setChartData] = useState({});
  const [summaryIntroImg, setSummaryIntroImg] = useState(null);
  const [sectionsIntroImg, setSectionsIntroImg] = useState(null);
  const [timQuestionsData, setTimQuestionsData] = useState({});
  const [timBlocksOverallData, setTimBlocksOverallData] = useState(null);
  const [blockNotes, setBlockNotes] = useState({});
  const [timFeedbackByBlock, setTimFeedbackByBlock] = useState({});
  const [dateCompleted, setDateCompleted] = useState();
  const [extraQ, setExtraQ] = useState();
  const [revieweeName, setRevieweeName] = useState("");
  const [summaryScores360, setSummaryScores360] = useState([]);
  const [responseRate360, setResponseRate360] = useState(null);

  const [blockReflections, setBlockReflections] = useState({});
  const [blockReflectionIds, setBlockReflectionIds] = useState({});
  const [timBlockReflections, setTimBlockReflections] = useState({});
  const [showAppendix, setShowAppendix] = useState(false);
  const [appendixPdfLoading, setAppendixPdfLoading] = useState(false);
  // Per-section scores for the MIM/LIM self-assessment appendix highlight,
  // surfaced from BlockReportIntro ([{name, positive}], excludes summary row).
  const [selfSectionsData, setSelfSectionsData] = useState(null);

  // Generate the appendix PDF on click (a complete, stable blob) rather than
  // eagerly via PDFDownloadLink, which can hand Chrome a half-built blob when
  // the report re-renders mid-download (file "downloads but won't open").
  const downloadAppendixPdf = async () => {
    if (appendixPdfLoading) return;
    setAppendixPdfLoading(true);
    try {
      const selfData = SELF_APPENDIX_DATA[currentAssessment?.slug];
      const safe = (s) => (s || "").replace(/[\\/:*?"<>|]/g, "").trim();
      let doc;
      let fileName;
      if (selfData) {
        doc = (
          <AppendixSelfPDF
            data={selfData}
            name={displayName}
            sectionsData={selfSectionsData}
          />
        );
        const who = safe(displayName);
        const prefix = currentAssessment.slug === "mim_self_assessment" ? "MIM" : "LIM";
        fileName = who
          ? `${prefix} Appendix - Professional Development Recommendations - ${who}.pdf`
          : `${prefix} Appendix - Professional Development Recommendations.pdf`;
      } else {
        doc = (
          <AppendixTIMPDF
            companyName={reportCompanyName}
            groupName={reportGroupName}
            blocksOverallData={timBlocksOverallData}
          />
        );
        const team = safe(currentGroup?.name);
        fileName = team
          ? `TIM Appendix - Professional Development Recommendations - ${team}.pdf`
          : `TIM Appendix - Professional Development Recommendations.pdf`;
      }
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error("Appendix PDF generation failed:", err);
      alert("Sorry, the appendix PDF failed to generate. Please try again.");
    } finally {
      setAppendixPdfLoading(false);
    }
  };

  const [fetchedGroup, setFetchedGroup] = useState(null);

  // Fetch the group by id when context-currentGroup is missing (admin preview, refresh, deep link, etc.)
  useEffect(() => {
    if (currentGroup?._id || !groupId) return;
    if (fetchedGroup?._id === groupId) return;
    let cancelled = false;
    (async () => {
      const res = await getGroupById(groupId);
      const g = res?.group || res;
      if (!cancelled && g?._id) setFetchedGroup(g);
    })();
    return () => { cancelled = true; };
  }, [groupId, currentGroup, fetchedGroup]);

  const reportGroupName = currentGroup?.name || fetchedGroup?.name || "";
  const reportCompanyName = currentGroup?.company?.name || fetchedGroup?.company?.name || "";

  // 360 raw data (no images needed -- PDF uses native vector charts)
  const [chartData360, setChartData360] = useState({});
  const [sectionsData360, setSectionsData360] = useState([]);

  // Determine which userId to use for data fetching.
  // - Admin preview: reportUserId from URL
  // - Logged-in user: currentUser._id
  // - Guest (share token): use viewerUserId from the share (the admin who
  //   created it) so existing userIdExists validators don't reject the request.
  //   The TIM report data itself is group-scoped, so userId doesn't change results.
  const effectiveUserId = reportUserId || currentUser?._id || guestViewerUserId;
  // Treat guests like admin-preview so the reflection inputs are read-only.
  const isAdminPreview = !!reportUserId || isGuest;

  useEffect(() => {
    const callCompleteDate = async () => {
      // For TIM team reports, pass groupId so the backend returns the latest
      // completion date across all team members instead of just this user's.
      const res = await getCompleteDate(effectiveUserId, slugAssessment, groupId);
      if (res) {
        const { dateCompleted: dateC } = res;
        if (dateC) {
          const d = new Date(dateC);
          setDateCompleted(d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
        } else {
          setDateCompleted(dateC);
        }
      }
    };
    callCompleteDate();
  }, [effectiveUserId, slugAssessment, groupId]);

  // Fetch reviewee name when admin is previewing a 360 report
  useEffect(() => {
    if (isAdminPreview) {
      const fetchRevieweeName = async () => {
        try {
          const res = await getUserEvalInfo(reportUserId);
          if (res?.user) {
            setRevieweeName(`${res.user.firstName} ${res.user.lastName || ""}`.trim());
          }
        } catch (err) {
          console.log("Error fetching reviewee info:", err);
        }
      };
      fetchRevieweeName();
    }
  }, [reportUserId, isAdminPreview]);

  useEffect(() => {
    if (!isLoading) {
      const assessment = getAssessmentBySlug(slugAssessment);
      if (!assessment) {
        // Assessment not in user context (e.g. admin previewing 360 report)
        // Fetch directly from API
        const fetchAssessment = async () => {
          try {
            const allAssessments = await getAllAssessment();
            const found = allAssessments?.find((a) => a.slug === slugAssessment);
            if (!found) {
              navigate("/");
              return;
            }
            setCurrentAssessment(found);
            setBlocks(found.blocks);
          } catch (err) {
            console.log("Error fetching assessment for report:", err);
            navigate("/");
          }
        };
        fetchAssessment();
        return;
      }
      setCurrentAssessment(assessment);
      setBlocks(assessment.blocks);
    }
  }, [slugAssessment, isLoading, getAssessmentBySlug, navigate]);

  useEffect(() => {
    if (currentAssessment && (currentGroup || groupId)) {
      callUserType();
    }
  }, [currentAssessment, currentUser, currentGroup, groupId]);

  const callUserType = async () => {
    const effectiveGroupId = groupId || currentGroup?._id;
    const lookupUserId = currentUser?._id || guestViewerUserId;
    if (!lookupUserId) return;
    const res = await getUserTIMRol(effectiveGroupId, lookupUserId);
    if (res) {
      calculateBlock(res.timRol[0]?.rol || "member");
    }
  };

  const calculateBlock = (rol) => {
    /* if(rol === "member" && blocks.length === currentAssessment?.blocks.length){ */
    const newBloks = [...currentAssessment?.blocks];
    setBlocks(newBloks);
    /* } */
  };

  const saveChartData = (blockId, data) => {
    setChartData((prev) => {
      if (prev[blockId]) return prev;
      return { ...prev, [blockId]: data };
    });
  };

  const pdfKey = useMemo(() => {
    const ids = Object.keys(chartData).sort();
    const notesIds = Object.keys(blockNotes).sort();
    const timReflIds = Object.keys(timBlockReflections).sort();

    const chartsPart =
      ids.length === 0
        ? "empty"
        : ids.map((id) => `${id}:${(chartData[id] || []).length}`).join("|");

    const notesPart =
      notesIds.length === 0
        ? "no-notes"
        : notesIds
            .map((id) => `${id}:${(blockNotes[id] ?? "").length}`)
            .join("|");

    const timReflPart =
      timReflIds.length === 0
        ? "no-tim-refl"
        : timReflIds
            .map((id) => `${id}:${(timBlockReflections[id] ?? "").length}`)
            .join("|");

    return `${chartsPart}__${notesPart}__${timReflPart}`;
  }, [chartData, blockNotes, timBlockReflections]);

  const pdfKey360 = useMemo(() => {
    const blockIds = Object.keys(chartData360).sort();
    if (blockIds.length === 0) return "empty-360";
    return blockIds
      .map((id) => `${id}:${(chartData360[id] || []).length}`)
      .join("|");
  }, [chartData360]);

  const timQuestionsCount = Object.keys(timQuestionsData).length;

  const timQuestionsReady =
    blocks.length > 0 && timQuestionsCount === blocks.length;

  const allTIMGlobalReady = !!timBlocksOverallData;

  const allTIMReady = timQuestionsReady && allTIMGlobalReady;

  // 360 readiness: all blocks have reported their data
  const chart360Count = Object.keys(chartData360).length;
  const all360Ready =
    (currentAssessment?.slug === "mim_360_assessment" ||
      currentAssessment?.slug === "lim_360_assessment") &&
    blocks.length > 0 &&
    chart360Count === blocks.length;

  // Display name: reviewee name for admin preview, current user when logged in,
  // share recipient name for guests viewing a shared report.
  const currentUserName = currentUser
    ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim()
    : "";
  const guestViewerName = share?.recipientName || share?.recipientEmail || "";
  const displayName = isAdminPreview && revieweeName
    ? revieweeName
    : currentUserName || guestViewerName;

  // Appendix wiring shared by the appendix view + download button below.
  const selfAppendixData = SELF_APPENDIX_DATA[currentAssessment?.slug];
  const appendixScoresReady = selfAppendixData
    ? !!selfSectionsData
    : !!timBlocksOverallData;

  // Group object for 360 report: use currentGroup if available, or create from groupId param
  const reportGroup = currentGroup || (groupId ? { _id: groupId } : null);

  // Admin report editor: per-team curation of TIM / E-TIM report text + verbatim
  // comments. Only admins can edit; the overlay is keyed by the group.
  const isTimFamilyReport = isTimFamilySlug(currentAssessment?.slug);
  const editGroupId = groupId || currentGroup?._id || null;
  const canEditReport = isTimFamilyReport && currentUser?.rol === "admin";
  const reportEdit = useReportEditValue(
    isTimFamilyReport ? editGroupId : null,
    canEditReport,
  );
  // The PDF reads verbatim from timFeedbackByBlock; admins receive RAW comments,
  // so curate them through the overlay here so an exported PDF matches the
  // client view. Idempotent for non-admins (already curated server-side).
  const curatedFeedbackByBlock = useMemo(() => {
    const out = {};
    for (const k of Object.keys(timFeedbackByBlock)) {
      out[k] = curateVerbatim(timFeedbackByBlock[k], reportEdit.overlay);
    }
    return out;
  }, [timFeedbackByBlock, reportEdit.overlay]);

  const handleBlockNoteChange = (blockId, text) => {
    setBlockNotes((prev) =>
      prev[blockId] === text ? prev : { ...prev, [blockId]: text },
    );
  };

  const handleExtraQChange = (text) => {
    setExtraQ(text);
  };

  const handleTimFeedbackReady = (blockId, feedbackArray) => {
    setTimFeedbackByBlock((prev) => {
      // Evita actualizar si es igual (opcional pero consistente con lo demas)
      const prevForBlock = prev[blockId] || [];
      if (prevForBlock.length === feedbackArray.length) {
        const same = prevForBlock.every(
          (item, i) => item.answer === feedbackArray[i].answer,
        );
        if (same) return prev;
      }
      return { ...prev, [blockId]: feedbackArray };
    });
  };

  return (
    <ReportEditContext.Provider value={reportEdit}>
      <ReportEditToolbar />
      <Container
      maxWidth="false"
      sx={{
        backgroundColor: "#e8f4f8",
        padding: "0px !important",
        overflowX: "hidden",
        maxWidth: "100vw",
      }}
    >
      {isLoading && <CircularProgress />}
      {currentAssessment && !isLoading && (
        <>
          {showAppendix && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                sx={{ padding: {xs:"12px 16px",sm:"14px 24px",md:"16px 48px"}, backgroundColor: "#fff" }}
              >
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon className="invertIcon" />}
                  onClick={() => { setShowAppendix(false); window.scrollTo(0, 0); }}
                >
                  BACK TO REPORT
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={appendixPdfLoading ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
                  disabled={appendixPdfLoading || !appendixScoresReady}
                  onClick={downloadAppendixPdf}
                >
                  {appendixPdfLoading
                    ? "Generating PDF..."
                    : !appendixScoresReady
                      ? "Preparing scores…"
                      : "DOWNLOAD APPENDIX PDF"}
                </Button>
              </Box>
              {selfAppendixData ? (
                <AppendixSelf data={selfAppendixData} sectionsData={selfSectionsData} />
              ) : (
                <AppendixTIM blocksOverallData={timBlocksOverallData} />
              )}
            </>
          )}
          {!showAppendix && (
          <>
          <Box
            display="flex"
            justifyContent={"space-between"}
            flexWrap="wrap"
            gap={1}
            sx={{ marginBottom: "0px", padding: {xs:"12px 16px",sm:"14px 24px",md:"16px 48px"}, backgroundColor: "#fff" }}
          >
            {(currentAssessment.slug === "mim_self_assessment" ||
              currentAssessment.slug === "lim_self_assessment") && (
              <>
                {Object.keys(chartData).length > 0 &&
                  Object.keys(chartData).length === blocks.length && (
                    <PDFDownloadLink
                      key={pdfKey}
                      document={
                        <AssessmentPDF
                          chartData={chartData}
                          blocks={blocks}
                          nameA={currentAssessment.name}
                          slug={currentAssessment.slug}
                          report={currentAssessment.report}
                          summaryIntroImg={summaryIntroImg}
                          name={displayName}
                          sectionsIntroImg={sectionsIntroImg}
                          info={currentAssessment?.report?.blocksInfo}
                          blockNotes={blockNotes}
                          dateCompleted={dateCompleted}
                          extraQ={extraQ}
                        />
                      }
                      fileName={`${slugAssessment}.pdf`}
                    >
                      {({ loading }) =>
                        loading ? (
                          <Button>
                            Generating PDF
                            <CircularProgress />
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<PrintIcon />}
                          >
                            DOWNLOAD
                          </Button>
                        )
                      }
                    </PDFDownloadLink>
                  )}
              </>
            )}
            {isTimFamilySlug(currentAssessment.slug) &&
              allTIMReady && (
                <>
                  <PDFDownloadLink
                    key={pdfKey}
                    document={
                      <AssessmentTIMPDF
                        report={currentAssessment.report}
                        name={displayName}
                        blocks={blocks}
                        questionsData={timQuestionsData}
                        blocksOverallData={timBlocksOverallData}
                        groupName={reportGroupName}
                        companyName={reportCompanyName}
                        info={currentAssessment?.report?.blocksInfo}
                        feedbackByBlock={curatedFeedbackByBlock}
                        textOverrides={reportEdit.overlay.texts}
                        dateCompleted={dateCompleted}
                        extraQ={extraQ}
                        blockReflections={timBlockReflections}
                        assessmentSlug={currentAssessment.slug}
                      />
                    }
                    fileName={(() => {
                      const safe = (s) => (s || "").replace(/[\\/:*?"<>|]/g, "").trim();
                      const team = safe(currentGroup?.name);
                      const date = safe(dateCompleted);
                      if (team && date) return `Teams in Motion Health Check Report – ${team} ${date}.pdf`;
                      if (team) return `Teams in Motion Health Check Report – ${team}.pdf`;
                      return `Teams in Motion Health Check Report.pdf`;
                    })()}
                  >
                    {({ loading }) =>
                      loading ? (
                        <Button>
                          Generating PDF... <CircularProgress />
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<PrintIcon />}
                        >
                          DOWNLOAD
                        </Button>
                      )
                    }
                  </PDFDownloadLink>
                </>
              )}
            {(currentAssessment.slug === "mim_360_assessment" ||
              currentAssessment.slug === "lim_360_assessment") &&
              all360Ready && (
                <PDFDownloadLink
                  key={pdfKey360}
                  document={
                    <Assessment360PDF
                      chartData360={chartData360}
                      blocks={blocks}
                      name={displayName}
                      slug={currentAssessment.slug}
                      report={currentAssessment.report || {}}
                      info={currentAssessment?.report?.blocksInfo || []}
                      sectionsData360={sectionsData360}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                      summaryScores360={summaryScores360}
                      responseRate={responseRate360}
                      blockReflections={blockReflections}
                    />
                  }
                  fileName={`${slugAssessment}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>
                        Generating PDF
                        <CircularProgress />
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PrintIcon />}
                      >
                        DOWNLOAD
                      </Button>
                    )
                  }
                </PDFDownloadLink>
              )}
            {!isGuest && (
              <Button
                component={Link}
                to={"/"}
                variant="contained"
                startIcon={<ArrowBackIcon className="invertIcon" />}
              >
                BACK
              </Button>
            )}
            {isGuest && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => { clearShare(); navigate("/"); }}
              >
                Sign out
              </Button>
            )}
          </Box>
          {isGuest && (
            <Box sx={{ backgroundColor: "#0F5385", color: "#fff", padding: { xs: "8px 16px", sm: "10px 24px", md: "12px 48px" } }}>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                Shared report — read-only access for {share.recipientEmail}
              </Typography>
            </Box>
          )}
          <Box display="flex" flexDirection="column">
            <BannerReportTIM image={true} text={`${currentAssessment?.name ?? ""} Report`} />
            <Box sx={{ padding: {xs:"12px 16px",sm:"14px 24px",md:"16px 48px"} }}>
              {isTimFamilySlug(currentAssessment?.slug) ? (
                <>
                  {reportCompanyName && (
                    <Typography variant="h5" color="primary" fontWeight={600}>Company: {reportCompanyName}</Typography>
                  )}
                  {reportGroupName && (
                    <Typography variant="h6" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>Team: {reportGroupName}</Typography>
                  )}
                  {dateCompleted && (
                    <Typography variant="h6" sx={{ color: "#69AF45", mt: 0.5 }}>Date Completed: {dateCompleted}</Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="h5" color="primary" fontWeight={600}>{displayName}</Typography>
                  {dateCompleted && <Typography variant="h6" sx={{ color: "#69AF45" }}>{dateCompleted}</Typography>}
                </>
              )}
            </Box>
            <WelcomeReport
              slug={currentAssessment?.slug}
              text={currentAssessment?.report?.welcome}
              mainImage={currentAssessment?.report?.mainImage}
              howToUse={currentAssessment?.report?.howToUse}
              sectionTitle={isTimFamilySlug(currentAssessment?.slug) ? "Team Effectiveness Health Check Report" : undefined}
            />
          </Box>
          {isTimFamilySlug(currentAssessment.slug) && (
            <BlockReportTIMSpecial
              onBlocksDataReady={setTimBlocksOverallData}
              groupId={groupId}
            />
          )}
          {(currentAssessment.slug === "mim_self_assessment" ||
            currentAssessment.slug === "lim_self_assessment") && (
            <BlockReportIntro
              slug={currentAssessment.slug}
              onSummaryChartReady={setSummaryIntroImg}
              onSectionsChartReady={setSectionsIntroImg}
              onSectionsDataReady={setSelfSectionsData}
            />
          )}
          {(currentAssessment.slug === "mim_360_assessment" ||
            currentAssessment.slug === "lim_360_assessment") && (
            <BlockReport360Intro
              slug={currentAssessment.slug}
              group={reportGroup}
              reportUserId={reportUserId}
              onSectionsDataReady={setSectionsData360}
              onSummaryScoresReady={setSummaryScores360}
              onResponseRateReady={setResponseRate360}
            />
          )}
          {blocks.map((b, i) => (
            <Fragment key={i}>
              <BannerReport number={i + 1} name={b.name} color={b.color} />
              {isTimFamilySlug(currentAssessment.slug) && (
                <BlockReportTIM
                  slug={b.slug}
                  blockId={b._id}
                  color={b.color}
                  info={currentAssessment?.report?.blocksInfo}
                  groupId={groupId}
                  assessmentSlug={currentAssessment.slug}
                  reportUserId={reportUserId}
                  readOnly={isGuest}
                  onQuestionsDataReady={(data) =>
                    setTimQuestionsData((prev) =>
                      prev[b._id] ? prev : { ...prev, [b._id]: data },
                    )
                  }
                  onFeedbackReady={(feedbackArray) =>
                    handleTimFeedbackReady(b._id, feedbackArray)
                  }
                  onReflectionReady={(bid, text) =>
                    setTimBlockReflections((prev) =>
                      prev[bid] === text ? prev : { ...prev, [bid]: text },
                    )
                  }
                />
              )}
              {(currentAssessment.slug === "mim_360_assessment" ||
                currentAssessment.slug === "lim_360_assessment") && (
                <>
                <BlockReport360
                  blockId={b._id}
                  name={b.name}
                  info={currentAssessment?.report?.blocksInfo}
                  slug={slugAssessment}
                  color={b.color}
                  reportUserId={reportUserId}
                  onBlock360DataReady={(questionsData) =>
                    setChartData360((prev) => {
                      return { ...prev, [b._id]: questionsData };
                    })
                  }
                />
                {/* Category Score & Reflection */}
                {summaryScores360.length > 0 && (() => {
                  const score = summaryScores360.find(s => String(s.blockId) === String(b._id));
                  if (!score) return null;
                  const pct = score.positive;
                  const rating = pct >= 90 ? "STRENGTH" : pct >= 60 ? "DEVELOPING" : "OPPORTUNITY";
                  const ratingColor = pct >= 90 ? "#69AF45" : pct >= 60 ? "#2B8DCC" : "#e67e22";
                  const reflectionSlug = `${currentAssessment.slug}_reflection_${b._id}`;
                  const isAdminPreview = !!reportUserId;
                  const loadReflection = async () => {
                    const res = await getExtraAnswer(reflectionSlug, reportUserId || currentUser._id);
                    if (res?.answer) {
                      setBlockReflections(prev => ({ ...prev, [b._id]: res.answer.answer }));
                      setBlockReflectionIds(prev => ({ ...prev, [b._id]: res.answer._id }));
                    }
                  };
                  if (!(b._id in blockReflections)) {
                    loadReflection();
                    setBlockReflections(prev => ({ ...prev, [b._id]: "" }));
                  }
                  const saveReflection = async () => {
                    const text = blockReflections[b._id] || "";
                    let res;
                    if (blockReflectionIds[b._id]) {
                      res = await updateAnswer(blockReflectionIds[b._id], { answer: text });
                    } else {
                      res = await createAnswer({ user: currentUser._id, answer: text, slug: reflectionSlug });
                    }
                    if (res?.answer) setBlockReflectionIds(prev => ({ ...prev, [b._id]: res.answer._id }));
                  };
                  return (
                    <Box sx={{ padding: {xs:"0px 16px",sm:"0px 24px",md:"0px 48px"}, margin: "24px 0 40px 0" }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: b.color || "#0F5385", mr: 1.5 }} />
                        <Typography variant="h6" color="secondary" fontWeight={600}>Category Score + Reflection</Typography>
                      </Box>
                      <Box sx={{ backgroundColor: "#F7FAFC", p: 3, borderLeft: `4px solid ${ratingColor}`, mb: 2 }}>
                        <Typography variant="body1" color="secondary" fontWeight={600} sx={{ fontSize: "1.1rem" }}>{b.name}</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{ color: ratingColor, mt: 1 }}>{rating}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Score: {pct.toFixed(0)}%</Typography>
                      </Box>
                      <Typography variant="body1" color="secondary" fontWeight={400} sx={{ fontSize: "1.05rem", lineHeight: "1.7", mb: 2 }}>
                        This category "{b.name}" has been identified {rating} for you. As you reviewed the scores on each question what stands out as a priority focus area in this dimension? Where you might want to focus?
                      </Typography>
                      <TextField
                        value={blockReflections[b._id] || ""}
                        onChange={(e) => setBlockReflections(prev => ({ ...prev, [b._id]: e.target.value }))}
                        fullWidth multiline rows={4}
                        placeholder="Write your reflection here..."
                        disabled={isAdminPreview}
                      />
                      {!isAdminPreview && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", mt: 1 }}>
                          <Button onClick={saveReflection} variant="contained" size="small" startIcon={<SaveIcon />}>Save</Button>
                          <SaveReminder/>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                </>
              )}
              {(currentAssessment.slug === "mim_self_assessment" ||
                currentAssessment.slug === "lim_self_assessment") && (
                <BlockReport
                  onChartDataReady={saveChartData}
                  info={currentAssessment?.report?.blocksInfo}
                  blockId={b._id}
                  color={b.color}
                  questionLength={b.questions.length}
                  onBlockNoteChange={handleBlockNoteChange}
                />
              )}
            </Fragment>
          ))}
          {
            <Fragment>
              {isTimFamilySlug(currentAssessment.slug) && (
                <BlockEndTIM
                  slug={currentAssessment.slug}
                  onExtraQChange={handleExtraQChange}
                />
              )}
              {(currentAssessment.slug === "mim_360_assessment" ||
                currentAssessment.slug === "lim_360_assessment") && (
                <>
                  <BannerReportTIM text={"Focus Areas for Development"} />
                  <BlockEnd360
                    slug={currentAssessment.slug}
                    onExtraQChange={handleExtraQChange}
                    reportUserId={reportUserId}
                    summaryScores360={summaryScores360}
                  />
                </>
              )}
              {(currentAssessment.slug === "mim_self_assessment" ||
                currentAssessment.slug === "lim_self_assessment") && (
                <>
                  <BannerReportTIM text={"Focus Areas for Development"} />
                  <BlockEndSelf
                    slug={currentAssessment.slug}
                    onExtraQChange={handleExtraQChange}
                  />
                </>
              )}
            </Fragment>
          }
          <Box
            display="flex"
            justifyContent={"center"}
            flexWrap="wrap"
            gap={1}
            sx={{ marginTop: "32px", padding: {xs:"12px 16px",sm:"14px 24px",md:"16px 48px"}, backgroundColor: "#fff" }}
          >
            {(currentAssessment.slug === "mim_self_assessment" ||
              currentAssessment.slug === "lim_self_assessment") && (
              <>
                {Object.keys(chartData).length > 0 &&
                  Object.keys(chartData).length === blocks.length && (
                    <PDFDownloadLink
                      key={`${pdfKey}-bottom`}
                      document={
                        <AssessmentPDF
                          chartData={chartData}
                          blocks={blocks}
                          nameA={currentAssessment.name}
                          slug={currentAssessment.slug}
                          report={currentAssessment.report}
                          summaryIntroImg={summaryIntroImg}
                          name={displayName}
                          sectionsIntroImg={sectionsIntroImg}
                          info={currentAssessment?.report?.blocksInfo}
                          blockNotes={blockNotes}
                          dateCompleted={dateCompleted}
                          extraQ={extraQ}
                        />
                      }
                      fileName={`${slugAssessment}.pdf`}
                    >
                      {({ loading }) =>
                        loading ? (
                          <Button>
                            Generating PDF
                            <CircularProgress />
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<PrintIcon />}
                            size="large"
                          >
                            DOWNLOAD REPORT
                          </Button>
                        )
                      }
                    </PDFDownloadLink>
                  )}
              </>
            )}
            {isTimFamilySlug(currentAssessment.slug) &&
              allTIMReady && (
                <PDFDownloadLink
                  key={`${pdfKey}-bottom`}
                  document={
                    <AssessmentTIMPDF
                      report={currentAssessment.report}
                      name={displayName}
                      blocks={blocks}
                      questionsData={timQuestionsData}
                      blocksOverallData={timBlocksOverallData}
                      groupName={reportGroupName}
                      companyName={reportCompanyName}
                      info={currentAssessment?.report?.blocksInfo}
                      feedbackByBlock={curatedFeedbackByBlock}
                      textOverrides={reportEdit.overlay.texts}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                      assessmentSlug={currentAssessment.slug}
                    />
                  }
                  fileName={(() => {
                    const safe = (s) => (s || "").replace(/[\\/:*?"<>|]/g, "").trim();
                    const team = safe(currentGroup?.name);
                    const date = safe(dateCompleted);
                    if (team && date) return `Teams in Motion Health Check Report – ${team} ${date}.pdf`;
                    if (team) return `Teams in Motion Health Check Report – ${team}.pdf`;
                    return `Teams in Motion Health Check Report.pdf`;
                  })()}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>
                        Generating PDF... <CircularProgress />
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PrintIcon />}
                        size="large"
                      >
                        DOWNLOAD REPORT
                      </Button>
                    )
                  }
                </PDFDownloadLink>
              )}
            {(isTimFamilySlug(currentAssessment.slug) ||
              SELF_APPENDIX_DATA[currentAssessment.slug]) && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => { setShowAppendix(true); window.scrollTo(0, 0); }}
              >
                Appendix: Module-by-Module Professional Development Recommendations
              </Button>
            )}
            {(currentAssessment.slug === "mim_360_assessment" ||
              currentAssessment.slug === "lim_360_assessment") &&
              all360Ready && (
                <PDFDownloadLink
                  key={`${pdfKey360}-bottom`}
                  document={
                    <Assessment360PDF
                      chartData360={chartData360}
                      blocks={blocks}
                      name={displayName}
                      slug={currentAssessment.slug}
                      report={currentAssessment.report || {}}
                      info={currentAssessment?.report?.blocksInfo || []}
                      sectionsData360={sectionsData360}
                      dateCompleted={dateCompleted}
                      extraQ={extraQ}
                      summaryScores360={summaryScores360}
                      responseRate={responseRate360}
                      blockReflections={blockReflections}
                    />
                  }
                  fileName={`${slugAssessment}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>
                        Generating PDF
                        <CircularProgress />
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PrintIcon />}
                        size="large"
                      >
                        DOWNLOAD REPORT
                      </Button>
                    )
                  }
                </PDFDownloadLink>
              )}
          </Box>
          </>
          )}
        </>
      )}
    </Container>
    </ReportEditContext.Provider>
  );
};

export default ReportAssessment;